"use client";

import { useCallback, useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "./Button";

/**
 * Copies `content` to the clipboard, showing a confirmation in place of the label.
 *
 * The confirmation carries the whole feedback: a copy leaves no visible trace, so
 * without it the only way to know the click registered is to paste and check.
 */
export function CopyButton({
  content,
  delay = 3000,
}: {
  content: string;
  delay?: number;
}) {
  const t = useTranslations();
  const [isCopied, setIsCopied] = useState(false);

  const copyContent = useCallback(async () => {
    // Ignored while the confirmation is up, so repeated clicks don't stack
    // timers and cut the feedback short.
    if (isCopied || !content) return;

    try {
      await navigator.clipboard.writeText(content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), delay);
    } catch (error) {
      console.error("Error copying content", error);
    }
  }, [isCopied, content, delay]);

  return (
    <Button variant="ghost" size="xs" onClick={copyContent}>
      {isCopied ? t("copied_feedback") : t("copy")}
    </Button>
  );
}
